import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FinFactorFundCard from "./FinFactorFundCard";
import { getTrendColor, hasTrend } from "../../utils/finfactorUi.utils";

const CategoryPanel = ({ name, data }) => {
  const [expanded, setExpanded] = useState(data.isExpanded || false);

  const hasValidValue = (value) => {
    if (value == null) return false;
    const normalizedValue = String(value).trim().toLowerCase();
    return !["", "-", "na", "n/a", "null", "undefined", "error"].includes(
      normalizedValue,
    );
  };

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      sx={{
        backgroundColor: "#1c2033",
        color: "white",
        boxShadow: "none",
        mb: 2,
        "&:before": { display: "none" },
        border: "1px solid rgba(255, 255, 255, 0.05)",
        borderRadius: "12px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#3b82f6", fontSize: "28px" }} />}
        sx={{
          backgroundColor: "#1c2033",
          pl: { xs: 2, md: 3 },
          pr: { xs: 2, md: 4 }, // Increased space from the right edge
          py: 2.5, // Even more air vertically
          "&.Mui-expanded": {
            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
          },
          "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": {
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 },
            margin: "0 !important", // Force override MUI's default expanded margins
            mr: { xs: 3, md: 7 },
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            marginLeft: { md: 2 }, // Explicit gap from metrics
          },
        }}
      >
        <Typography
          variant="h6"
          fontWeight="700"
          sx={{
            fontSize: { xs: "16px", md: "18px" },
            textTransform: "capitalize",
          }}
        >
          {`${name.toLowerCase()} (${data.count || 0})`}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.5, md: 3 }}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          sx={{
            width: { xs: "100%", md: "auto" },
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          {/* Current Value Column */}
          <Box sx={{ 
            textAlign: { xs: "left", md: "right" }, 
            minWidth: { md: "110px" },
            display: { xs: "flex", md: "block" },
            flexDirection: { xs: "row", md: "column" },
            justifyContent: { xs: "space-between", md: "flex-end" },
            alignItems: { xs: "center", md: "flex-end" },
            gap: { xs: 2, md: 0 }
          }}>
            <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", display: "block", mb: { xs: 0, md: 0.5 } }}>
              Current Value
            </Typography>
            <Box sx={{ textAlign: "inherit" }}>
              <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, fontWeight: "700", color: "white", lineHeight: 1 }}>
                {data.totalValue}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  mt: 0.5,
                  color: getTrendColor(data.change, data.isPositive),
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                {hasTrend(data.change, data.isPositive) && (
                  data.isPositive ? <ArrowDropUpIcon sx={{ fontSize: 18 }} /> : <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                )}
                {data.change} {data.change !== "-" && `(${String(data.changePercentage || "0").replace("+", "")})`}
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderLeft: "1px solid rgba(255, 255, 255, 0.1)", alignSelf: "stretch", mx: { md: 1 }, display: { xs: "none", md: "block" } }} />

          {/* Purchase Cost Column */}
          <Box sx={{ 
            textAlign: { xs: "left", md: "right" }, 
            minWidth: { md: "110px" },
            display: { xs: "flex", md: "block" },
            flexDirection: { xs: "row", md: "column" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 2, md: 0 }
          }}>
            <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", display: "block", mb: { xs: 0, md: 0.5 } }}>
              Purchase Cost
            </Typography>
            <Box sx={{ textAlign: "inherit" }}>
              <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, fontWeight: "700", color: "white", lineHeight: 1 }}>
                {data.purchaseCost || "-"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderLeft: "1px solid rgba(255, 255, 255, 0.1)", alignSelf: "stretch", mx: { md: 1 }, display: { xs: "none", md: "block" } }} />

          {/* Gain Column */}
          <Box sx={{ 
            textAlign: { xs: "left", md: "right" }, 
            minWidth: { md: "110px" },
            display: { xs: "flex", md: "block" },
            flexDirection: { xs: "row", md: "column" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 2, md: 0 }
          }}>
            <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", display: "block", mb: { xs: 0, md: 0.5 } }}>
              Gain (CAGR)
            </Typography>
            <Box
              sx={{
                textAlign: "inherit",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: "700",
                lineHeight: 1,
                color: getTrendColor(data.gain, data.isGainPositive),
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {data.gain === "-" ? "-" : (
                <>
                  {hasTrend(data.gain, data.isGainPositive) && (
                    data.isGainPositive ? <ArrowDropUpIcon sx={{ fontSize: 20 }} /> : <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                  )}
                  {data.gain} ({String(data.gainPercentage || "0%").replace("+", "")})
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 1.5, md: 3 },
          py: 3,
          backgroundColor: "rgba(255, 255, 255, 0.01)",
        }}
      >
        {data.items && data.items.length > 0 ? (
          <Box
            sx={{
              maxHeight: "450px",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <Stack spacing={0}>
              {data.items.map((fund) => (
                <Box
                  key={fund.id}
                  sx={{
                    "& > div > div:first-of-type": {
                      display: "none",
                    },
                  }}
                >
                  {(() => {
                    const hasIsin = hasValidValue(fund.isin);
                    const hasFolio = hasValidValue(fund.folio);
                    const folioText = hasFolio
                      ? `${hasIsin ? " | " : ""}Folio No. : ${fund.folio}`
                      : "";

                    return (
                  <FinFactorFundCard
                    {...fund}
                    status="1"
                    folio={`${hasIsin ? `ISIN : ${fund.isin}` : ""}${folioText}`}
                  />
                    );
                  })()}
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography
            color="rgba(255, 255, 255, 0.4)"
            textAlign="center"
            py={2}
          >
            No funds in this category
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CategoryPanel;
